import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, Modal, Text} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import DeleteNovaAgentCategoryMutation from '../../../../mutations/nova/DeleteNovaAgentCategoryMutation';
import {toRelayGlobalId} from '../shared/relayGlobalId';

export interface DeleteNovaCategoryModalProps {
	categoryId: string;
	categoryName: string;
	// Number of agents currently in this category — they move to Uncategorised as part of the
	// delete (the server does the reassignment; see DeleteNovaAgentCategoryMutation.ts).
	agentCount: number;
	// Fired after the delete mutation succeeds, so the caller (NovaStudioShell.tsx) can refetch
	// the catalogue and pick up both the removed category and the reassigned agents.
	onDeleted: () => void;
	onClose: () => void;
}

export function DeleteNovaCategoryModal({
	categoryId,
	categoryName,
	agentCount,
	onDeleted,
	onClose,
}: DeleteNovaCategoryModalProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [deleting, setDeleting] = useState(false);
	const [deleteFailed, setDeleteFailed] = useState(false);

	const handleConfirm = (): void => {
		if (deleting) {
			return;
		}
		setDeleting(true);
		setDeleteFailed(false);
		Util.CommitMutation(
			DeleteNovaAgentCategoryMutation,
			{id: toRelayGlobalId('NovaAgentCategory', categoryId)},
			() => {
				onDeleted();
				onClose();
			},
			true,
			null,
			(transaction, onFailure) => {
				// Call the fallback so the standard error modal still fires, and keep this modal
				// open with its own inline error so the author can retry without losing context.
				// A true return means the fallback scheduled a retry of this same mutation rather
				// than surfacing an error — the delete is still in flight, so stay in the deleting
				// state and let the retry's own success/failure decide, instead of showing an
				// inline failure over a delete that may be about to succeed.
				if (onFailure(transaction) === true) {
					return;
				}
				setDeleting(false);
				setDeleteFailed(true);
			}
		);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={onClose} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'nova.deleteCategory.title'}, {categoryName})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'m'}>
					<Text data-cy="nova-delete-category-message">
						{agentCount > 0
							? formatMessage(
									{
										id: 'nova.deleteCategory.messageWithCount',
									},
									{count: agentCount}
							  )
							: formatMessage({
									id: 'nova.deleteCategory.messageEmpty',
							  })}
					</Text>
					{deleteFailed && (
						<Text color="error" data-cy="nova-delete-category-error">
							{formatMessage({
								id: 'nova.deleteCategory.submitFailed',
							})}
						</Text>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleConfirm}
					aria-disabled={deleting}
					dataCy="nova-confirm-delete-category"
				>
					{formatMessage({id: 'nova.deleteCategory.confirmButton'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onClose} dataCy="nova-cancel-delete-category">
					{formatMessage({id: 'nova.deleteCategory.cancelButton'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
